/**
 * Client-side store for an active support-impersonation session.
 *
 * The minted "act as user" token is kept SEPARATE from the admin's real Nhost session: it lives in
 * sessionStorage (PER-TAB, auto-expires) and the identity surfaces (Apollo auth link, AuthContext,
 * useJwtClaims, SelectionContext) prefer it while present. It is never routed through
 * `nhost.refreshSession` — it has no refresh token and must die at `exp`.
 *
 * This replaces the old approach of swapping/backing-up the real session in the shared cookie/
 * localStorage, which clobbered the admin's own session across ALL tabs and needed a sentinel
 * refresh token. Because the overlay is per-tab, the admin's other tabs keep their real session.
 */
export interface ImpersonationUser {
  id: string
  email: string
  displayName: string
  avatarUrl?: string
  defaultRole: string
  metadata?: Record<string, unknown>
  roles?: { role: string }[]
}

export interface ImpersonationSession {
  accessToken: string
  /** ISO timestamp; once past, the session is dropped on read. */
  expiresAt: string
  /** The acted-as user (for UI chrome + tenant/property selection). */
  user: ImpersonationUser
}

const KEY = 'taskyinn.impersonation'
type Listener = () => void
const listeners = new Set<Listener>()

function emit(): void {
  listeners.forEach((l) => l())
}

/** Current session, or null if absent/expired (expired entries are evicted as a side effect). */
export function getImpersonation(): ImpersonationSession | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(KEY)
    if (!raw) return null
    const session = JSON.parse(raw) as ImpersonationSession
    if (!session.accessToken || new Date(session.expiresAt).getTime() <= Date.now()) {
      window.sessionStorage.removeItem(KEY)
      return null
    }
    return session
  } catch {
    return null
  }
}

export function setImpersonation(session: ImpersonationSession): void {
  if (typeof window === 'undefined') return
  window.sessionStorage.setItem(KEY, JSON.stringify(session))
  emit()
}

export function clearImpersonation(): void {
  if (typeof window === 'undefined') return
  window.sessionStorage.removeItem(KEY)
  emit()
}

/** Subscribe to start/stop changes (for React effects). Returns an unsubscribe fn. */
export function subscribeImpersonation(fn: Listener): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}
